import type { Ingredient, OptionalKind } from '../data/types';

// One place that decides how each flavor of "optional" is labelled and colored,
// so a step card and the ingredient ledger can never drift apart.

export type OptionalMeta = {
  kind: OptionalKind;
  label: string;   // short chip label
  blurb: string;   // fallback explanation when a recipe gives no optionalWhy
  fg: string;      // text color
  bg: string;      // tint background
  bd: string;      // border color
};

const META: Record<OptionalKind, Omit<OptionalMeta, 'kind'>> = {
  garnish: {
    label: 'at the table',
    blurb: 'Goes on at the table. Skipping it changes nothing about the cooking.',
    fg: '#5c6b4a',
    bg: '#eef2e6',
    bd: '#c8d4b6',
  },
  upgrade: {
    label: 'flavor upgrade',
    blurb: 'Not required, but it genuinely changes the dish if you add it.',
    fg: '#8a5a2b',
    bg: '#f6ecdd',
    bd: '#e0c9a6',
  },
  'serve-with': {
    label: 'serve with',
    blurb: 'A separate component, cooked alongside rather than added to the pot.',
    fg: '#6a4a75',
    bg: '#f1eaf4',
    bd: '#d5c2dc',
  },
  'at-reheat': {
    label: 'add at reheat',
    blurb: 'Deliberately held back from the freezer and stirred in later.',
    fg: '#2f6785',
    bg: '#e3eef5',
    bd: '#b9d3e2',
  },
};

export function optionalMeta(ing: Ingredient): OptionalMeta {
  const kind: OptionalKind = ing.optionalKind ?? 'upgrade';
  return { kind, ...META[kind] };
}

export function isOptional(ing: Ingredient | undefined): boolean {
  return Boolean(ing?.optional);
}

/** The sentence shown under an optional ingredient. Recipe text wins. */
export function optionalReason(ing: Ingredient): string {
  return ing.optionalWhy ?? optionalMeta(ing).blurb;
}
